import { Plane, Clock, Calendar, AlertTriangle, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ARRIVAL_FLIGHT } from '../data/arrivalFlight'

interface ArrivalNotificationModalProps {
  open: boolean
  onDismiss: () => void
  onPlaceManually: () => void
  onAutoFit: () => void
}

export function ArrivalNotificationModal({
  open,
  onDismiss,
  onPlaceManually,
  onAutoFit,
}: ArrivalNotificationModalProps) {
  const f = ARRIVAL_FLIGHT

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onDismiss()}>
      <DialogContent className="sm:max-w-md p-0 overflow-hidden gap-0">
        {/* Header strip */}
        <div className="bg-blue-600 px-5 py-4 text-white">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2 text-blue-100 text-xs font-medium uppercase tracking-wider">
              <Plane className="w-3.5 h-3.5" />
              FlightBridge — Incoming Request
            </div>
            <button onClick={onDismiss} className="text-blue-200 hover:text-white transition-colors">
              <X className="w-4 h-4" />
            </button>
          </div>
          <DialogHeader>
            <DialogTitle className="text-white text-xl font-bold mt-1">
              {f.tailNumber} — {f.typeLabel}
            </DialogTitle>
          </DialogHeader>
          <p className="text-blue-100 text-sm mt-0.5">{f.clientName}</p>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-4">
          {/* Flight details row */}
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-1.5 text-muted-foreground">
              <Plane className="w-3.5 h-3.5" />
              <span>from {f.origin}</span>
            </div>
            <div className="flex items-center gap-1.5 text-muted-foreground">
              <Clock className="w-3.5 h-3.5" />
              <span>ETA {f.etaLabel}</span>
            </div>
            <div className="flex items-center gap-1.5 text-muted-foreground">
              <Calendar className="w-3.5 h-3.5" />
              <span>{f.nights} nights</span>
            </div>
          </div>

          {/* Space challenge badge */}
          <div className="flex items-start gap-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
            <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-amber-900 dark:text-amber-300">Space Challenge</p>
              <p className="text-xs text-amber-700 dark:text-amber-400 mt-0.5">
                {f.wingspanFt}ft wingspan · {f.lengthFt}ft length · {f.heightFt}ft tail height
              </p>
            </div>
            <Badge variant="secondary" className="shrink-0 text-xs bg-amber-100 dark:bg-amber-900 text-amber-800 dark:text-amber-300 border-0">
              Ultra Long Range
            </Badge>
          </div>

          {/* Revenue callout */}
          <div className="flex items-center justify-between bg-muted/50 rounded-lg px-3 py-2">
            <span className="text-sm text-muted-foreground">Potential Revenue</span>
            <span className="text-sm font-bold text-green-600 dark:text-green-400">
              ${f.ratePerNight.toLocaleString()}/night · ${f.totalRevenue.toLocaleString()} total
            </span>
          </div>
        </div>

        {/* Action buttons */}
        <div className="px-5 pb-5 flex gap-3">
          <Button
            variant="outline"
            className="flex-1"
            onClick={onPlaceManually}
          >
            Place Manually
          </Button>
          <Button
            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold"
            onClick={onAutoFit}
          >
            ✦ AutoFit
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
